import type { Point } from '../../../common/maths/geometry/pointTypes';
import { getPagePositionFromClientPosition, getRelativePosition } from '../domUtil';

const DEFAULT_POINT: Point = { x: 0, y: 0 };

export type DomEventContainer = Pick<HTMLElement, 'getBoundingClientRect'> | null | undefined;

type PagePositionLikeEvent = {
    pageX?: number | null;
    pageY?: number | null;
    clientX?: number | null;
    clientY?: number | null;
};

type GetPagePositionFn<EventType> = (event: EventType) => Point | null | undefined;

/**
 * Returns the page position as an { x, y } object.
 */
export const getMouseEventPagePosition = (event: PagePositionLikeEvent): Point => {
    if (typeof event?.pageX === 'number' && typeof event?.pageY === 'number') {
        return {
            x: event.pageX,
            y: event.pageY,
        };
    }

    if (typeof event?.clientX === 'number' && typeof event?.clientY === 'number') {
        return (getPagePositionFromClientPosition({
            x: event.clientX,
            y: event.clientY,
        }) || DEFAULT_POINT) as Point;
    }

    return DEFAULT_POINT;
};

/**
 * Creator function for finding the relative position of an event.
 */
export const createGetRelativePositionFromEventFn =
    <EventType>(pagePositionGetterFn: GetPagePositionFn<EventType>) =>
    (event: EventType, containerDomNode: DomEventContainer): Point | undefined => {
        const pagePosition = pagePositionGetterFn(event);
        return getRelativePosition(pagePosition, containerDomNode);
    };

/**
 * Gets the relative position in a specific DOM node for a mouse event.
 */
export const getRelativePositionFromDomEvent = createGetRelativePositionFromEventFn(getMouseEventPagePosition);
